import React, {useEffect, useState} from "react";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { formatISO, isValid } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Dialog from '@mui/material/Dialog';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useSxStyles as classes } from "../styles/sxStyles";
import API from "../http/API";
import {Alert, AlertTitle} from "@mui/material";
import { useRequestUrl, useAuthToken, useCurrentUser } from "../context/contextHooks";
import Download from "@mui/icons-material/DownloadDoneTwoTone";
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import ImageSearchTwoToneIcon from '@mui/icons-material/ImageSearchTwoTone';
import ReplayIcon from "@mui/icons-material/Replay";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from '@mui/material/Radio';
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid2 from '@mui/material/Grid2';

const UserReceipts = () => {
    const [user,] = useCurrentUser();
    const [open, setOpen] = React.useState(false)
    const [isDownloading, setIsDownloading] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [requestUrl,] = useRequestUrl();
    const [authToken,] = useAuthToken();
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [fileResponse, setFileResponse] = useState(null);
    const [receipts, setReceipts] = useState(null);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [error, setError] = useState(null);

    const handleOpen = () => {
        clearData();
        handleRetrieve();
        setOpen(true);
    }

    const startDateChanged = (value) => {
        if (isValid(value)) setStartDate(value);
    }

    const endDateChanged = (value) => {
        if (isValid(value)) setEndDate(value);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const clearData = () => {
        setError(null);
        setReceipts(null);
        setSelectedReceipt(null);
        setActionResponse(null);
        setFileResponse(null);
    }

    const handleRetrieve = () => {
        clearData();
        setLoading(true);
        const data = JSON.stringify({
            requestType: 'vtUserPayments',
            paymentOperator: user.externalId,
            startDate: formatISO(startDate, { representation: 'date' }),
            endDate: formatISO(endDate, { representation: 'date' })
        });
        API.postRetrieve(`${requestUrl}/retrieveReceipts`, data, 'retrieveReceipts', `Bearer ${authToken}`, setActionResponse);
    }

    const handleDownload = () => {
        setFileResponse(null);
        if (selectedReceipt) {
            setError(null);
            if (!isDownloading) {
                const selected = receipts.filter(rcpt => rcpt.transactionId === selectedReceipt);
                if (selected && selected.length > 0) {
                    const courtId = selected[0].courtId;
                    setIsDownloading(true);
                    const request = JSON.stringify({courtId: courtId, transactionId: selectedReceipt});
                    API.postForFile(`${requestUrl}/getReceiptPdf`, request, 'getReceipt', authToken, setFileResponse);
                }
            } else setError('Error retrieving receipt');
        } else setError('Please select a payment to download');
    }

    const scrollToAlert = () => {
        window.scrollTo(0, 40);
    };

    useEffect(() => {
        if (actionResponse && loading) {
            if (actionResponse.error) {
                setError(actionResponse.errorMessage);
            } else if (actionResponse.requestType === 'retrieveReceipts') {
                if (!actionResponse.data || actionResponse.data.length < 1) {
                    setError('No payments found for this date range');
                } else setReceipts(actionResponse.data);
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (isDownloading && fileResponse) {
            if (fileResponse.type === 'application/json') {
                const blb = new Blob([fileResponse], {type: "application/json"});
                const reader = new FileReader();
                reader.onload = function () {
                    const text = reader.result;
                    const textResponse = JSON.parse(text);
                    setError(textResponse.message ? textResponse.message : 'An error occurred retrieving the receipt');
                    scrollToAlert();
                }
                reader.readAsText(blb);
            } else {
                const file = new Blob([fileResponse], {type: "application/pdf"});
                file.name = 'paymentReceipt.pdf'
                const fileUrl = window.URL.createObjectURL(file);
                let link = document.createElement('a');
                link.href = fileUrl;
                link.download = 'CitePayReceipt.pdf';
                link.click();
                setTimeout(() => {
                    window.URL.revokeObjectURL(fileUrl);
                }, 100);
            }
            setIsDownloading(false);
        }
    }, [fileResponse])

    return (<>
        <Button variant="contained"
                color="primary"
                startIcon={<ReceiptLongTwoToneIcon/>}
                sx={classes.squashedButton}
                onClick={handleOpen}
        >
            Retrieve a Receipt
        </Button>
        { open &&
            <Dialog
                disableEscapeKeyDown
                fullWidth
                maxWidth='md'
                open={open}
                onClose={handleClose}
                sx={classes.dialog}>
                <DialogTitle style={{paddingBottom: '0'}}>
                    <Typography variant="h6" color='primary'>
                        Retrieve a Receipt
                    </Typography>
                </DialogTitle>
                <DialogTitle style={{paddingBottom: '0'}}>
                    <Typography variant="h5" color='primary'>
                    </Typography>
                </DialogTitle>
                { error &&
                    <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                        <AlertTitle>Error!</AlertTitle>
                        {error}
                    </Alert>
                }
                <DialogContent dividers sx={{margin: '1rem .5rem'}}>
                    <Grid2 container spacing={2} alignItems='top' >
                        <Grid2 sx={{padding: '.5rem'}} size={6}>
                            <FormControl fullWidth>
                                <FormLabel shrink={true} sx={{...classes.inputLabel}}>
                                    <Typography component='span' sx={classes.displayLabel}>Start Date</Typography>
                                </FormLabel>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        name='startDate'
                                        disableMaskedInput={false}
                                        format='MM/dd/yyyy'
                                        value={startDate}
                                        onChange={(newValue) => {
                                            startDateChanged(newValue);
                                        }}
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    sx: classes.textbox
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid2>
                        <Grid2 sx={{padding: '.5rem'}} size={6}>
                            <FormControl fullWidth>
                                <FormLabel shrink={true} sx={{...classes.inputLabel}}>
                                    <Typography component='span' sx={classes.displayLabel}>End Date</Typography>
                                </FormLabel>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        name='endDate'
                                        disableMaskedInput={false}
                                        format='MM/dd/yyyy'
                                        value={endDate}
                                        onChange={(newValue) => {
                                            endDateChanged(newValue);
                                        }}
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    sx: classes.textbox
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid2>
                        { receipts && receipts.length > 0 &&
                            <Grid2 sx={{padding: '1rem', paddingBottom: '0'}} size={12}>
                                <List sx={{...classes.list, maxHeight: 'auto', overflow: 'auto'}}>
                                    <ListItem alignItems='flex-start' sx={classes.listPadding}>
                                        <ListItemText
                                            sx={classes.listText}
                                            primary={
                                                <Grid2
                                                    container
                                                    spacing={1}
                                                    alignItems="top"
                                                >
                                                    <Grid2 size={1.5}>&nbsp;</Grid2>
                                                    <Grid2 size={2}>Time</Grid2>
                                                    <Grid2 size={3}>Payor</Grid2>
                                                    <Grid2 size={4}>Description</Grid2>
                                                    <Grid2 sx={{textAlign: 'right'}} size={1.5}>Amount</Grid2>
                                                </Grid2>
                                            }
                                            slotProps={{
                                                primary: {
                                                    color: 'textPrimary'
                                                }
                                            }}
                                        />
                                    </ListItem>
                                    <RadioGroup name='selected' value={selectedReceipt} onChange={(e) => {setSelectedReceipt(e.target.value)}}>
                                        <>
                                            { receipts.map((pymt) => {
                                                return (
                                                    (<ListItem alignItems='flex-start' disableGutters sx={{paddingTop: '0', paddingBottom: '0'}}>
                                                        <ListItemText
                                                            sx={classes.listText}
                                                            primary={
                                                                <Grid2
                                                                    container
                                                                    spacing={1}
                                                                    alignItems="top"
                                                                >
                                                                    <Grid2 size={1.5}>
                                                                        <FormControlLabel
                                                                            value={pymt.transactionId}
                                                                            control={
                                                                                <Radio
                                                                                    size='small'
                                                                                    sx={{marginLeft: '1rem', paddingBottom: '0', paddingLeft: '.5rem', paddingTop: '0', position: ''}}
                                                                                />
                                                                            }
                                                                            label='Select'
                                                                            sx={{paddingLeft: '0', paddingTop: '0'}}
                                                                        />
                                                                    </Grid2>
                                                                    <Grid2 size={2}>{pymt.paymentTimeDisplay}</Grid2>
                                                                    <Grid2 size={3}>{pymt.payor}</Grid2>
                                                                    <Grid2 size={4}>{pymt.description}</Grid2>
                                                                    <Grid2 sx={{textAlign: 'right', paddingRight: '.5rem'}} size={1.5}>{pymt.totalDisplay}</Grid2>
                                                                </Grid2>
                                                            }
                                                            slotProps={{
                                                                primary: {
                                                                    color: 'textPrimary'
                                                                }
                                                            }}
                                                        />
                                                    </ListItem>)
                                                );
                                            })}
                                        </>
                                    </RadioGroup>
                                </List>
                            </Grid2>
                        }
                    </Grid2>
                </DialogContent>
                <DialogActions sx={{margin: '0 1rem .5rem 1rem', padding: '0 .5rem'}}>
                    <Button variant="outlined"
                            color="primary"
                            startIcon={<ReplayIcon/>}
                            sx={classes.button}
                            onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ImageSearchTwoToneIcon/>}
                        sx={classes.button}
                        onClick={handleRetrieve}
                    >
                        Retrieve Payments
                    </Button>
                    { receipts && selectedReceipt &&
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Download/>}
                            sx={classes.button}
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? "Downloading..." : "Download"}
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        }
    </>);
}

export default UserReceipts;
